import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

import { copyTextToClipboard } from './index';

interface DebugReportFallbackDialogProps {
  reportText: string | null;
  onOpenChange: (open: boolean) => void;
}

export function DebugReportFallbackDialog({
  reportText,
  onOpenChange,
}: DebugReportFallbackDialogProps) {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [copyError, setCopyError] = useState<string | null>(null);
  const open = reportText !== null;

  useEffect(() => {
    if (!open) return;
    setCopyError(null);
    window.requestAnimationFrame(() => {
      textAreaRef.current?.focus({ preventScroll: true });
      textAreaRef.current?.select();
    });
  }, [open, reportText]);

  async function copyFromDialog() {
    if (!reportText) return;
    try {
      await copyTextToClipboard(reportText, { target: textAreaRef.current });
      toast.success('Debug report copied.');
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setCopyError(`Copy is still blocked: ${message}`);
      textAreaRef.current?.focus({ preventScroll: true });
      textAreaRef.current?.select();
    }
  }

  function selectReportText() {
    textAreaRef.current?.focus({ preventScroll: true });
    textAreaRef.current?.select();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Copy debug report manually</DialogTitle>
          <DialogDescription>
            Your browser blocked automated clipboard access. The safe debug
            report is selected below; use Copy report, or copy the selected text
            with your browser or keyboard.
          </DialogDescription>
        </DialogHeader>
        {copyError && (
          <p className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-sm">
            {copyError}
          </p>
        )}
        <Textarea
          ref={textAreaRef}
          readOnly
          value={reportText ?? ''}
          aria-label="Debug report text"
          className="max-h-[50vh] min-h-64 font-mono text-xs"
          onFocus={selectReportText}
        />
        <DialogFooter>
          <Button type="button" variant="outline" onClick={selectReportText}>
            Select text
          </Button>
          <Button type="button" onClick={() => void copyFromDialog()}>
            Copy report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
